// Root.jsx

import React, { useEffect, useRef, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useStartupStatus } from 'context/StartupStatusContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { useBaseInviteAccept } from 'context/BaseInviteAcceptContext';

// Routing
import { Outlet, useLocation } from 'react-router-dom';

// Utilities
import { defaultFont } from 'utilities/Defaults';

// Services
import { captureInviteTokenFromSearch } from 'services/bases/baseInviteRedirectStorage';

// Hooks
import usePreloadUserNotifications from 'hooks/usePreloadUserNotifications';
import useConfirmLoginStatus from 'root/hooks/useConfirmLoginStatus';
import usePreloadActiveBase from 'root/hooks/usePreloadActiveBase';
import usePreloadSettings from 'root/hooks/usePreloadSettings';
import usePreloadUserEngagements from 'root/hooks/usePreloadUserEngagements';

// CSS
import './Root.css';

// Components
import { RootStyles } from './RootStyles';
import VerifyEmailGate from 'components/auth/VerifyEmailGate';
import Progress from 'components/progress/Progress';
import SplashScreen from 'components/splash/SplashScreen';
import Login from 'login/Login';
import { isWorkspaceSidebarPath } from 'workspace/oauth/workspaceAuthPaths';

/**
 * Root
 *
 * @returns {ReactNode} The rendered component.
 */
const Root = () => {

  // ----- CONTEXT ----- //

  // Global Context

  const {
    loginOpen,
    setLoginOpen,
  } = useUI();

  const { postAuthRedirect, setPostAuthRedirect } = useNavigation();
  // ----- HOOKS ----- //

  const { status: inviteAcceptStatus, errorMessage: inviteAcceptErrorMessage } = useBaseInviteAccept();

  // ----- CONTEXT ----- //

  // User Context

  const { user, isAuthLoading, isEmailVerified } = useUser();
  const { activeBase } = useBase();
  // ----- HOOKS ----- //

  const { hasActiveStatus } = useStartupStatus();

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- ROUTING ----- //

  // Routing

  const location = useLocation();

  // ----- REFS ----- //

  // Refs

  const promptedRootLoginRef = useRef(false);
  // ----- STATE ----- //

  const [hasBootedAppShell, setHasBootedAppShell] = useState(false);

  // HOOKS
  useConfirmLoginStatus();
  usePreloadUserEngagements();
  usePreloadUserNotifications();
  usePreloadSettings();
  usePreloadActiveBase();

  // ----- EFFECTS ----- //

  // When an unauthenticated user lands on a deep link (e.g. /node/:id),
  // remember the target path and prompt for login/signup.

  useEffect(() => {
    captureInviteTokenFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (user) return;

    const path = location.pathname + location.search + location.hash;
    const onWorkspaceRail = isWorkspaceSidebarPath(location.pathname);

    // Remember where to return after auth. The Workspace rail always wins over a
    // stale stored redirect (e.g. a previous /admin deep link in localStorage).
    if (path && path !== '/') {
      if (onWorkspaceRail || !postAuthRedirect) {
        if (postAuthRedirect !== path) {
          setPostAuthRedirect(path);
        }
      }
      if (!onWorkspaceRail) {
        setLoginOpen(true);
      }
    }
  }, [isAuthLoading, user, location.pathname, location.search, location.hash, postAuthRedirect, setPostAuthRedirect, setLoginOpen]);

  useEffect(() => {
    if (!user || inviteAcceptStatus !== 'error' || !inviteAcceptErrorMessage) return;
    console.error('[Root] pending base invite acceptance failed:', inviteAcceptErrorMessage);
  }, [inviteAcceptErrorMessage, inviteAcceptStatus, user]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (user) return;
    if (location.pathname !== '/') return;
    if (promptedRootLoginRef.current) return;

    promptedRootLoginRef.current = true;
    setLoginOpen(true);
  }, [isAuthLoading, user, location.pathname, setLoginOpen]);

  // Keep the desktop Login modal closed while the rail owns auth.
  useEffect(() => {
    if (!isWorkspaceSidebarPath(location.pathname)) return;
    if (user) return;
    setLoginOpen(false);
  }, [location.pathname, setLoginOpen, user]);

  useEffect(() => {
    if (!user) {
      setHasBootedAppShell(false);
      return;
    }

    if (!isAuthLoading && activeBase) {
      setHasBootedAppShell(true);
    }
  }, [activeBase, isAuthLoading, user]);

  const onWorkspaceRail = isWorkspaceSidebarPath(location.pathname);
  const shouldShowVerifyEmailGate = Boolean(user) && !isEmailVerified && !onWorkspaceRail;
  const shouldShowSplashScreen = !shouldShowVerifyEmailGate
    && !onWorkspaceRail
    && !hasBootedAppShell
    && (isAuthLoading || (Boolean(user) && !activeBase && hasActiveStatus));
  const shouldRenderOutlet = onWorkspaceRail
    ? !isAuthLoading
    : Boolean(user);

  // ----- RENDER ----- //

  return (
    <>
      <RootStyles
        fontFamily={defaultFont}
        placeholderColor={theme.foregroundMuted}
        scrollbarColor={theme.foregroundMuted}
      />

      {!shouldShowVerifyEmailGate && !onWorkspaceRail && (
        <Login
          isOpen={loginOpen}
          setOpen={setLoginOpen}
        />
      )}

      {shouldShowVerifyEmailGate ? (
        <VerifyEmailGate />
      ) : shouldShowSplashScreen ? (
        <SplashScreen />
      ) : shouldRenderOutlet ? (
        <Outlet />
      ) : null}

      <Progress />
    </>
  );
};

// Dev Tools
Root.displayName = 'Root';

export default Root;
